/**
 * Embed Language Gate (spec 075 — Phase 3, FR-3/FR-4).
 *
 * Pre-chat language chooser shown inside the embedded widget BEFORE the chat
 * initialises, when the agent's embed settings require it. Nothing else loads
 * until a language is tapped; the choice becomes the session locale (chrome
 * lock + direction + agent reply default).
 */

import { AgentIcon } from '../agents/agent-icon'
import { LOCALE_LABELS, getChromeStrings, setEmbedLocale } from '@/lib/embed-locale'

/** Locales rendered right-to-left in the gate heading. */
const RTL_GATE_LOCALES = new Set(['ar', 'he', 'fa', 'ur'])

interface EmbedLanguageGateProps {
  /** Locales offered, in display order (e.g. ["ar", "en"]). */
  languages: string[]
  /** Agent display name shown above the choice. */
  agentName?: string | null
  /** Agent avatar URL. */
  agentAvatar?: string | null
  /** Called after the locale is stored, so the page can proceed to the chat. */
  onSelected: (locale: string) => void
}

/**
 * Full-panel language chooser. Bilingual by design: each option shows its own
 * native name (a visitor who can't read the current UI language must still be
 * able to find theirs), and the heading shows both gate strings when two
 * languages are offered.
 */
export function EmbedLanguageGate({
  languages,
  agentName,
  agentAvatar,
  onSelected,
}: EmbedLanguageGateProps) {
  const handlePick = (locale: string) => {
    setEmbedLocale(locale)
    onSelected(locale)
  }

  return (
    <div className="flex size-full flex-col items-center justify-center bg-background p-6 text-center">
      {(agentAvatar || agentName) && (
        <div className="mb-4 h-20 w-20 overflow-hidden rounded-full">
          <AgentIcon
            avatar={agentAvatar ?? null}
            name={agentName ?? ''}
            size="xl"
            className="h-20 w-20"
          />
        </div>
      )}
      {agentName && (
        <p className="font-heading mb-1 text-[22px] font-medium leading-tight text-foreground">
          {agentName}
        </p>
      )}

      {/* Heading shows every offered language's own "choose your language",
          each in its native script and direction, so a visitor who can't read
          the others can still find the prompt in a language they know. */}
      <div className="mb-6 mt-2 space-y-0.5">
        {languages.map((locale) => (
          <p
            key={locale}
            className="text-sm text-muted-foreground"
            dir={RTL_GATE_LOCALES.has(locale) ? 'rtl' : 'ltr'}
            lang={locale}
          >
            {getChromeStrings(locale).chooseLanguage}
          </p>
        ))}
      </div>

      <div className="flex w-full max-w-xs gap-2.5">
        {languages.map((locale) => {
          const label = LOCALE_LABELS[locale]
          return (
            <button
              key={locale}
              type="button"
              onClick={() => handlePick(locale)}
              lang={locale}
              className="flex-1 rounded-xl border border-border bg-background px-3 py-3.5 text-[15px] font-medium text-foreground transition-colors hover:border-primary hover:bg-primary/10"
            >
              <span className="block">{label?.native ?? locale}</span>
              {label && label.native !== label.english && (
                <span className="mt-0.5 block text-[11px] font-normal text-muted-foreground">
                  {label.english}
                </span>
              )}
            </button>
          )
        })}
      </div>
    </div>
  )
}
